"use client";

import * as React from "react";
import { Check, ChevronDown, X, Search } from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { useLocalization } from "@/context/localization-context";

export interface MultiSelectOption {
  label: string;
  value: string;
}

interface MultiSelectProps {
  options: MultiSelectOption[];
  selected: string[];
  onChange: (selected: string[]) => void;
  placeholder?: string;
  className?: string;
  maxDisplay?: number;
  enableSearch?: boolean;
  searchPlaceholder?: string;
  label?: string;
  showClearAll?: boolean;
  showSelectAll?: boolean;
  selectedCountLabel?: (count: number) => string;
  selectAllLabel?: string;
  clearAllLabel?: string;
  noOptionsLabel?: string;
  noResultsLabel?: string;
}

export function MultiSelect({
  options,
  selected,
  onChange,
  placeholder: placeholderProp,
  className,
  maxDisplay = 3,
  enableSearch = false,
  searchPlaceholder: searchPlaceholderProp,
  label,
  showClearAll = true,
  showSelectAll = false,
  selectedCountLabel,
  selectAllLabel: selectAllLabelProp,
  clearAllLabel: clearAllLabelProp,
  noOptionsLabel: noOptionsLabelProp,
  noResultsLabel: noResultsLabelProp,
}: MultiSelectProps) {
  // default texts in the language of the UI
  const { t, tp } = useLocalization();
  const placeholder = placeholderProp ?? t("list.multiSelect.placeholder");
  const searchPlaceholder = searchPlaceholderProp ?? t("list.multiSelect.searchPlaceholder");
  const selectAllLabel = selectAllLabelProp ?? t("list.multiSelect.selectAll");
  const clearAllLabel = clearAllLabelProp ?? t("common.clearAll");
  const noOptionsLabel = noOptionsLabelProp ?? t("list.multiSelect.noOptions");
  const noResultsLabel = noResultsLabelProp ?? t("list.multiSelect.noResults");
  const [isOpen, setIsOpen] = React.useState(false);
  const [searchQuery, setSearchQuery] = React.useState("");
  const [selectedIndex, setSelectedIndex] = React.useState(-1);
  const searchInputRef = React.useRef<HTMLInputElement>(null);

  // Filter options based on search query
  const filteredOptions = React.useMemo(() => {
    if (!enableSearch || !searchQuery.trim()) return options;
    
    const query = searchQuery.toLowerCase().trim();
    return options.filter(option => 
      option.label.toLowerCase().includes(query) ||
      option.value.toLowerCase().includes(query)
    );
  }, [options, searchQuery, enableSearch]);

  // Reset search when dropdown closes
  React.useEffect(() => {
    if (!isOpen) {
      setSearchQuery("");
      setSelectedIndex(-1);
    }
  }, [isOpen]);

  const handleSelect = (value: string) => {
    if (selected.includes(value)) {
      onChange(selected.filter((item) => item !== value));
    } else {
      onChange([...selected, value]);
    }
  };

  const stopRemoveEvent = (event: React.SyntheticEvent) => {
    event.preventDefault();
    event.stopPropagation();
  };

  const handleRemove = (value: string, event: React.SyntheticEvent) => {
    stopRemoveEvent(event);
    onChange(selected.filter((item) => item !== value));
  };

  const handleSelectAll = () => {
    onChange(filteredOptions.map(option => option.value));
  };

  const handleClearAll = () => {
    onChange([]);
  };

  const selectedOptions = options.filter((option) => 
    selected.includes(option.value)
  );

  const displayText = () => {
    if (selected.length === 0) {
      return placeholder;
    }
    
    if (selected.length <= maxDisplay) {
      return null; // Show badges instead
    }
    
    return selectedCountLabel
      ? selectedCountLabel(selected.length)
      : tp("list.multiSelect.selected", selected.length);
  };

  return (
    <DropdownMenu open={isOpen} onOpenChange={setIsOpen}>
      <DropdownMenuTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={isOpen}
          className={cn(
            "justify-between min-h-9 h-auto",
            className
          )}
        >
          <div className="flex flex-wrap gap-1 flex-1">
            {selected.length === 0 ? (
              <span className="text-muted-foreground">{placeholder}</span>
            ) : selected.length <= maxDisplay ? (
              selectedOptions.map((option) => (
                <Badge
                  key={option.value}
                  variant="secondary"
                  className="text-xs h-5 px-2 hover:bg-secondary/80"
                >
                  {option.label}
                  <X
                    aria-label={t("list.multiSelect.remove", { label: option.label })}
                    role="button"
                    tabIndex={0}
                    className="ml-1 h-3 w-3 cursor-pointer hover:text-destructive"
                    onPointerDown={(event) => stopRemoveEvent(event)}
                    onMouseDown={(event) => stopRemoveEvent(event)}
                    onClick={(e) => handleRemove(option.value, e)}
                    onKeyDown={(event) => {
                      if (event.key === "Enter" || event.key === " ") {
                        handleRemove(option.value, event);
                      }
                    }}
                  />
                </Badge>
              ))
            ) : (
              <span className="text-sm">{displayText()}</span>
            )}
          </div>
          <ChevronDown className="h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent 
        className="w-56 p-0"
        align="start"
      >
        {label && (
          <DropdownMenuLabel className="text-xs text-muted-foreground">
            {label} {searchQuery.trim() ? 
              t("list.multiSelect.countFiltered", { shown: filteredOptions.length, total: options.length }) : 
              `(${options.length})`
            }
          </DropdownMenuLabel>
        )}
        
        {/* Search Input */}
        {enableSearch && options.length > 5 && (
          <>
            <div className="px-2 py-1">
              <div className="relative">
                <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
                <Input
                  ref={searchInputRef}
                  placeholder={searchPlaceholder}
                  value={searchQuery}
                  onChange={(e) => {
                    setSearchQuery(e.target.value);
                    setSelectedIndex(-1);
                  }}
                  className="h-8 pl-8 text-sm"
                  autoFocus
                  onClick={(e) => e.stopPropagation()}
                  onKeyDown={(e) => {
                    if (e.key === 'Escape') {
                      e.preventDefault();
                      setSearchQuery("");
                      setIsOpen(false);
                    } else if (e.key === 'ArrowDown') {
                      e.preventDefault();
                      if (filteredOptions.length > 0) {
                        setSelectedIndex(0);
                      }
                    } else if (e.key === 'Enter' && filteredOptions.length > 0) {
                      e.preventDefault();
                      const option = selectedIndex >= 0 ? filteredOptions[selectedIndex] : filteredOptions[0];
                      handleSelect(option.value);
                    }
                    e.stopPropagation();
                  }}
                />
              </div>
            </div>
            <DropdownMenuSeparator />
          </>
        )}

        {/* Select All / Clear All Actions */}
        {(showSelectAll || showClearAll) && (
          <>
            <div className="px-2 py-1 flex gap-1">
              {showSelectAll && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-6 px-2 text-xs"
                  onClick={handleSelectAll}
                  disabled={filteredOptions.every(option => selected.includes(option.value))}
                >
                  {selectAllLabel}
                </Button>
              )}
              {showClearAll && selected.length > 0 && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-6 px-2 text-xs"
                  onClick={handleClearAll}
                >
                  {clearAllLabel}
                </Button>
              )}
            </div>
            <DropdownMenuSeparator />
          </>
        )}

        {/* Options List */}
        <div 
          className="max-h-64 overflow-y-auto"
          onKeyDown={(e) => {
            if (e.key === 'ArrowDown') {
              e.preventDefault();
              setSelectedIndex(prev => 
                prev < filteredOptions.length - 1 ? prev + 1 : 0
              );
            } else if (e.key === 'ArrowUp') {
              e.preventDefault();
              setSelectedIndex(prev => 
                prev > 0 ? prev - 1 : filteredOptions.length - 1
              );
            } else if (e.key === 'Enter' && selectedIndex >= 0) {
              e.preventDefault();
              const option = filteredOptions[selectedIndex];
              if (option) {
                handleSelect(option.value);
              }
            }
          }}
          tabIndex={-1}
        >
          {filteredOptions.length === 0 ? (
            <div className="p-2 text-sm text-muted-foreground text-center">
              {searchQuery.trim() ? noResultsLabel : noOptionsLabel}
            </div>
          ) : (
            filteredOptions.map((option, index) => (
              <DropdownMenuItem
                key={option.value}
                onSelect={(event) => {
                  event.preventDefault();
                  handleSelect(option.value);
                }}
                className={cn(
                  "cursor-pointer",
                  index === selectedIndex && "bg-accent text-accent-foreground"
                )}
                onMouseEnter={() => setSelectedIndex(index)}
              >
                <div className="flex items-center space-x-2 w-full">
                  <div
                    className={cn(
                      "flex h-4 w-4 items-center justify-center rounded-sm border",
                      selected.includes(option.value)
                        ? "bg-primary text-primary-foreground border-primary"
                        : "border-input"
                    )}
                  >
                    {selected.includes(option.value) && (
                      <Check className="h-3 w-3" />
                    )}
                  </div>
                  <span className="truncate">{option.label}</span>
                </div>
              </DropdownMenuItem>
            ))
          )}
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
